/**
 * @constrigo/auth-client — rafraîchissement silencieux de session, partagé par
 * les fronts web (SaaS, Admin, Ops, Architect, TravauxBTP).
 *
 * Le jeton d'accès dure 1 h, le refresh 30 j *glissants* (re-daté à chaque
 * rotation). Une session ne devrait donc jamais mourir tant que l'utilisateur
 * revient sous 30 j : toute déconnexion observée avant est un défaut du chemin
 * de rafraîchissement, pas une expiration légitime. Ce module rassemble en un
 * seul endroit ce que chaque app réimplémentait — ou n'implémentait pas du tout.
 *
 * `apps/mobile` reste sur sa propre implémentation (jetons porteurs en
 * SecureStore, hors du modèle cookie), mais applique les mêmes règles.
 */
import type { AuthClientConfig, AuthedFetchOptions, RefreshOutcome } from "./types";
import { createCookieReaders } from "./cookies";
import { createCrossTab, type CrossTabMessage } from "./crosstab";
import { createRefresher } from "./refresh";
import { createAuthedFetch, isUnauthorizedResponse } from "./fetch";
import { startProactiveRefresh } from "./schedule";

export type { AuthClientConfig, AuthedFetchOptions, RefreshOutcome, CrossTabMessage };
export { isUnauthorizedResponse };

export interface AuthClient {
  /**
   * Rafraîchit la session (dédupliqué intra- et inter-onglets).
   * Trois issues, à ne surtout pas confondre — cf. `RefreshOutcome`.
   */
  tryRefreshSession(): Promise<RefreshOutcome>;
  /** `fetch` qui absorbe l'expiration du jeton (401 et 207 masquant un 401). */
  createAuthedFetch(options: AuthedFetchOptions): typeof fetch;
  /** Jeton CSRF courant (double-submit), relu à chaque appel. */
  readCsrf(): string | null;
  /** Expiration du jeton d'accès (epoch ms), `null` si aucune session. */
  readSessionExpiry(): number | null;
  /**
   * Une session existe-t-elle encore ? Vrai même après l'expiration du jeton
   * d'accès, tant que le refresh est valide — c'est le signal qui autorise à
   * tenter `auth.me` au lieu d'afficher d'emblée un écran déconnecté.
   */
  hasSession(): boolean;
  /** Démarre le renouvellement proactif ; retourne la fonction d'arrêt. */
  startProactiveRefresh(onSessionLost?: () => void): () => void;
  /** Déconnexion : purge l'horodatage partagé et prévient les autres onglets. */
  markLoggedOut(): void;
  /** Écoute les événements d'authentification émis par les autres onglets. */
  subscribeCrossTab(handler: (message: CrossTabMessage) => void): () => void;
}

export function createAuthClient(config: AuthClientConfig): AuthClient {
  const namespace = config.namespace ?? "constrigo";
  const cookies = createCookieReaders(config.cookieScope);
  const crossTab = createCrossTab(namespace);
  const refresher = createRefresher(config, crossTab);

  return {
    tryRefreshSession: refresher.tryRefreshSession,

    createAuthedFetch(options) {
      return createAuthedFetch(refresher.tryRefreshSession, options);
    },

    readCsrf: cookies.readCsrf,
    readSessionExpiry: cookies.readSessionExpiry,

    hasSession() {
      return cookies.readSessionExpiry() !== null;
    },

    startProactiveRefresh(onSessionLost) {
      return startProactiveRefresh({
        readSessionExpiry: cookies.readSessionExpiry,
        tryRefreshSession: refresher.tryRefreshSession,
        onSessionLost,
      });
    },

    markLoggedOut: refresher.markLoggedOut,
    subscribeCrossTab: crossTab.subscribe,
  };
}
